/** Small presentational helpers shared across the AP tabs. */
import {
  ArrowLeftCircleIcon as ArrowLeftCircleOutlineIcon,
  CheckCircleIcon as CheckCircleOutlineIcon,
  ClockIcon as ClockOutlineIcon,
  ExclamationCircleIcon as ExclamationCircleOutlineIcon,
  XCircleIcon as XCircleOutlineIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  ArrowLeftCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import { type ReactNode } from "react";
import { Badge } from "@/core/components/ui/badge";
import { Button, type ButtonProps } from "@/core/components/ui/button";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import {
  Typography,
  typographyVariants,
} from "@/core/components/ui/typography";
import type { ApAgentAction } from "@/accounts-payable/types";
import {
  CONFIDENCE_TEXT_CLASS,
  confidenceLabel,
  confidenceTone,
  pricebookCostChange,
} from "@/accounts-payable/utils";
import { cn } from "@/core/utils/utils";

type BadgeVariant =
  | "green"
  | "blue"
  | "purple"
  | "magenta"
  | "gold"
  | "red"
  | "orange"
  | "aqua"
  | "gray";

/**
 * Bright status pill — reserved for state (Applied, Rejected, Matched, …),
 * driven by one of the maps in utils.ts.
 */
export const StatusBadge = ({
  meta,
  size = "sm",
}: {
  meta: { variant: BadgeVariant; label: string };
  size?: "sm" | "md" | "lg";
}) => (
  <Badge variant={meta.variant} size={size}>
    {meta.label}
  </Badge>
);

/**
 * An empty circle drawn to the same radius and stroke as the heroicons outline
 * circles (CheckCircle etc.), which have no plain-circle icon of their own.
 */
const OpenCircleIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    aria-hidden="true"
    className={className}
  >
    <circle cx="12" cy="12" r="9" />
  </svg>
);

/** Compact outcome glyph for a resolved action, rollback-aware. `outline`
 * swaps the solid heroicon for its outline peer (reads better at a larger size). */
export const ActionStatusGlyph = ({
  action,
  className = "h-4 w-4 flex-none",
  outline = false,
}: {
  action: ApAgentAction;
  className?: string;
  outline?: boolean;
}) => {
  const Rollback = outline ? ArrowLeftCircleOutlineIcon : ArrowLeftCircleIcon;
  const Exclaim = outline
    ? ExclamationCircleOutlineIcon
    : ExclamationCircleIcon;
  const XCircle = outline ? XCircleOutlineIcon : XCircleIcon;
  const Clock = outline ? ClockOutlineIcon : ClockIcon;
  const Check = outline ? CheckCircleOutlineIcon : CheckCircleIcon;

  if (action.rollback_status === "rolled_back")
    return <Rollback className={cn(className, "text-tertiary-text")} />;
  // A rollback failure is often a legitimate "can't reverse, data moved on" and
  // is Admin-only territory, so it reads as muted info, not a red error.
  if (action.rollback_status === "rollback_failed")
    return <Exclaim className={cn(className, "text-tertiary-text")} />;
  if (action.status === "rejected")
    return <XCircle className={cn(className, "text-red-60")} />;
  // An apply failure means the change never landed — the one failure the user
  // should notice, so it stays red.
  if (action.status === "apply_failed")
    return <Exclaim className={cn(className, "text-red-60")} />;
  if (action.status === "approved" || action.status === "applying")
    return <Clock className={cn(className, "text-blue-60")} />;
  // Not yet decided: an open checkbox, so a pending row reads as a to-do in
  // the same list as the resolved ones.
  if (action.status === "pending_review")
    return <OpenCircleIcon className={cn(className, "text-tertiary-text")} />;
  // rolling_back/rollback_blocked intentionally fall through to the applied
  // glyph — transients that settle on their own, so no separate mark.
  return <Check className={cn(className, "text-green-60")} />;
};

/**
 * Footer "Load more" for the paginated lists. Render it only while another
 * page exists, and disable it while any fetch is in flight — fetchNextPage
 * would cancel an in-flight refetch and leave stale rows on screen.
 */
export const LoadMoreButton = ({
  onClick,
  loading,
  disabled,
}: {
  onClick: () => void;
  loading: boolean;
  disabled?: boolean;
}) => (
  <Stack direction="row" justify="center" fullWidth className="flex-none">
    <Button
      variant="secondary"
      size="sm"
      onClick={onClick}
      loading={loading}
      disabled={disabled}
    >
      Load more
    </Button>
  </Stack>
);

/**
 * Loading / error body for the detail sidebars, which fetch their record by
 * id — the panel is already open while that fetch is in flight, so it needs
 * its own close affordance and states.
 */
export const DetailSidebarFallback = ({
  loading,
  errorMessage,
  onClose,
}: {
  loading: boolean;
  errorMessage: string;
  onClose: () => void;
}) => (
  <Stack gap="lg" fullWidth className="h-full p-6">
    <Stack direction="row" justify="end" fullWidth>
      <DetailCloseButton onClose={onClose} />
    </Stack>
    {loading ? (
      <Stack gap="md" fullWidth>
        <Skeleton className="h-5 w-24 rounded-full" />
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </Stack>
    ) : (
      <span className="text-md text-tertiary-text">{errorMessage}</span>
    )}
  </Stack>
);

/** Close ✕ for the AP detail sidebars — same treatment as the Spiffs sidebars. */
export const DetailCloseButton = ({ onClose }: { onClose: () => void }) => (
  <Button
    variant="unstyled"
    aria-label="Close"
    onClick={onClose}
    className="flex-none rounded-sm text-secondary-text hover:text-primary-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
  >
    <XMarkIcon className="size-6" />
  </Button>
);

/**
 * "87% confidence" in its traffic-light colour: green ≥90%, amber 70–90%, red
 * below. Semibold at every tone — at this size colour alone lets the green and
 * amber recede — and the % is always spelled out, so colour is never the only
 * signal.
 */
export const ConfidenceLabel = ({
  value,
  className,
}: {
  value: number;
  className?: string;
}) => (
  <Typography
    variant="captionW1SemiBold"
    className={cn(CONFIDENCE_TEXT_CLASS[confidenceTone(value)], className)}
  >
    {confidenceLabel(value)}
  </Typography>
);

/**
 * Heading for a top-level section of a detail panel (Document, Activity…).
 * Same weight as the section headings in the Spiffs sidebars.
 */
export const SectionHeading = ({ children }: { children: ReactNode }) => (
  <Typography variant="subtitleW2">{children}</Typography>
);

/**
 * Quiet label for a group inside a section (Reason, Line items, Comments).
 * Matches the label style of the core `InfoWithLabel`.
 */
export const SectionLabel = ({ children }: { children: ReactNode }) => (
  <Typography variant="subtitleW3" textColor="tertiary">
    {children}
  </Typography>
);

/**
 * Shown while an agent run exists but hasn't produced proposals yet — chiefly
 * the window right after a manual job match, which enqueues a run server-side.
 * The surrounding query polls, so this resolves into the action list on its own.
 */
export const AgentRunningNotice = () => (
  <Stack
    direction="row"
    gap="sm"
    align="center"
    role="status"
    className="rounded-lg border border-stroke-tertiary px-3 py-2"
  >
    <ClockIcon className="h-4 w-4 flex-none animate-pulse text-tertiary-text" />
    <span className="text-md text-secondary-text">
      The agent is running against this invoice. Proposals appear here when it
      finishes.
    </span>
  </Stack>
);

/**
 * Signed percent change of a pricebook cost, colored by direction. Renders
 * nothing when there is no current cost to compare against.
 */
export const CostChangePct = ({
  current,
  proposed,
  suffix,
  className,
}: {
  current: number;
  proposed: number;
  /** Trailing label, e.g. "vs current". */
  suffix?: string;
  className?: string;
}) => {
  const { direction, pct } = pricebookCostChange(current, proposed);
  if (pct === null) return null;

  const sign = pct > 0 ? "+" : ""; // negative values already have a sign
  return (
    <span
      className={cn(
        // green-70 (6.8:1 on white), the step --text-interactive now uses;
        // green-60 passed AA too (4.8:1), this keeps the two greens in step.
        "font-semibold",
        {
          "text-negative-text": direction === "raise",
          "text-green-70": direction === "lower",
        },
        className,
      )}
    >
      {`${sign}${pct.toFixed(1)}%${suffix ? ` ${suffix}` : ""}`}
    </span>
  );
};

/**
 * A text-only button (Comment, Cancel, Show all): 13px semibold from the
 * `subtitleW3` variant, green or grey, with a visible focus ring. Kept quieter
 * than the pill buttons so a card's decision stays its loudest control. Uses
 * the variant's classes rather than <Typography>, which renders a <p> — not
 * valid inside a <button>.
 */
export const TextButton = ({
  tone = "interactive",
  className,
  children,
  ...props
}: Omit<ButtonProps, "variant"> & { tone?: "interactive" | "secondary" }) => (
  <Button
    variant="unstyled"
    className={cn(
      typographyVariants({ variant: "subtitleW3" }),
      "w-fit rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
      {
        "text-interactive-text hover:underline": tone === "interactive",
        "text-secondary-text hover:text-primary-text": tone === "secondary",
      },
      className,
    )}
    {...props}
  >
    {children}
  </Button>
);
